import { Box, Grid, Typography, Slider, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Maps, LoadingSpinner } from "@components";
import moment from "moment";

const RouteHistoryMap = (props) => {
	const { routeData, truckData, startDate, endDate, loading } = props;

	// SLIDER STATE
	const [sliderTimestamp, setSliderTimestamp] = useState(null);
	const [displayedRoute, setDisplayedRoute] = useState(null); // The Filtered Data for Map
	const [isPlaying, setIsPlaying] = useState(false);

	const minTime = moment(startDate).startOf('day').unix();
	const maxTime = moment(endDate).endOf('day').unix();
	const isMultiDay = moment(endDate).diff(moment(startDate), 'days') > 0;

	// When new data arrives, reset slider to end
	useEffect(() => {
		if (routeData && Array.isArray(routeData) && routeData.length > 0) {
			const lastRecordTs = parseInt(routeData[routeData.length - 1].timestamp);
			// Default to the actual last data point available in the trace
			setSliderTimestamp(lastRecordTs || maxTime);
		} else {
			setDisplayedRoute(null);
		}
	}, [routeData, startDate, endDate]);

	// Filter route when slider or data changes
	useEffect(() => {
		if (!routeData || !Array.isArray(routeData)) {
			setDisplayedRoute(null);
			return;
		}

		if (sliderTimestamp === null) {
			setDisplayedRoute(routeData);
			return;
		}

		// FILTER LOGIC
		const filtered = routeData.filter(point => parseInt(point.timestamp) <= sliderTimestamp);

		if (filtered.length > 0) {
			const lastPoint = filtered[filtered.length - 1];
			// Attach metadata for the map's truck indicator
			filtered.direction = lastPoint.direction || 0;
			filtered.lat = lastPoint.lat;
			filtered.lng = lastPoint.lng;
			filtered.speed = lastPoint.speed;
			filtered.timestamp = lastPoint.timestamp;

			setDisplayedRoute(filtered);
		} else {
			setDisplayedRoute(null);
		}
	}, [sliderTimestamp, routeData]);

	// PLAYBACK EFFECT
	useEffect(() => {
		let interval;
		if (isPlaying && routeData && routeData.length > 0) {
			const effectiveMax = parseInt(routeData[routeData.length - 1].timestamp);
			const effectiveMin = parseInt(routeData[0].timestamp);
			
			// Adaptive step size: cover the whole range in roughly 30-60 seconds
			const rangeSize = effectiveMax - effectiveMin;
			const stepSize = Math.max(60, Math.floor(rangeSize / 300)); // At least 1 min, but scale up for long ranges

			interval = setInterval(() => {
				setSliderTimestamp((prev) => {
					const current = prev === null ? effectiveMin : prev;
					const nextVal = current + stepSize; 
					if (nextVal >= effectiveMax) {
						setIsPlaying(false);
						return effectiveMax;
					}
					return nextVal;
				});
			}, 100);
		}
		return () => clearInterval(interval);
	}, [isPlaying, routeData]);

	const handleSliderChange = (event, newValue) => {
		setSliderTimestamp(newValue);
	};

	const handlePlayPause = () => {
		setIsPlaying(!isPlaying);
	};

	const handleReset = () => {
		setSliderTimestamp(minTime);
		setIsPlaying(false);
	};

	const formatTimeLabel = (unix) => {
		return moment.unix(unix).format(isMultiDay ? "DD MMM HH:mm" : "hh:mm A");
	};

	if (loading) return <LoadingSpinner />;
	
	// Validation for route data content (coordinates check)
	const hasValidCoords = routeData && routeData.some(p => p.lat !== 0 && p.lng !== 0);

	if (!routeData || routeData.length === 0) return (
		<Box sx={{ p: 10, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 4, border: '2px dashed #cbd5e1' }}>
			<Typography variant="h6" color="textSecondary" fontWeight={700}>NO ROUTE DATA FOUND</Typography>
			<Typography variant="body2" color="textSecondary">Selected vehicle has no recorded movement for this period.</Typography>
		</Box>
	);

	return (
		<Grid container spacing={2}>
			{/* TIME SLIDER SECTION */}
			<Grid item xs={12} sx={{ px: 2, py: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
					<Box sx={{ textAlign: 'center', minWidth: 80 }}>
						<Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', display: 'block' }}>START</Typography>
						<Typography variant="body2" sx={{ fontWeight: 900 }}>
							{moment.unix(minTime).format(isMultiDay ? "DD/MM" : "HH:mm")}
						</Typography>
					</Box>

					<Slider
						value={sliderTimestamp || maxTime}
						min={minTime}
						max={maxTime}
						step={60} 
						onChange={handleSliderChange}
						valueLabelDisplay="auto"
						valueLabelFormat={formatTimeLabel}
						sx={{ flexGrow: 1, '& .MuiSlider-thumb': { width: 24, height: 24 } }}
					/>

					<Box sx={{ textAlign: 'center', minWidth: 80 }}>
						<Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', display: 'block' }}>END</Typography>
						<Typography variant="body2" sx={{ fontWeight: 900 }}>
							{moment.unix(maxTime).format(isMultiDay ? "DD/MM" : "HH:mm")}
						</Typography>
					</Box>

					<Button 
						variant="contained" 
						size="large" 
						onClick={handlePlayPause} 
						color={isPlaying ? "secondary" : "primary"} 
						sx={{ borderRadius: '12px', minWidth: 120, fontWeight: 800 }}
						startIcon={isPlaying ? null : <span style={{ fontSize: '20px' }}>▶</span>}
					>
						{isPlaying ? "PAUSE" : "PLAY ROUTE"}
					</Button>

					<Button variant="outlined" size="large" onClick={handleReset} sx={{ borderRadius: '12px', fontWeight: 800 }}>
						RESET
					</Button>
				</Box>
				
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mt: 1 }}>
					<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
						Total Points: <strong>{routeData.length}</strong>
					</Typography>
					<Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 800, bgcolor: 'primary.lighter', px: 2, py: 0.5, borderRadius: 1 }}>
						Playback Time: {sliderTimestamp ? formatTimeLabel(sliderTimestamp) : formatTimeLabel(maxTime)}
					</Typography>
					{!hasValidCoords && (
						<Typography variant="caption" sx={{ color: 'error.main', fontWeight: 800 }}>
							⚠️ WARNING: VEHICLE GPS POSITIONING UNAVAILABLE (0,0)
						</Typography>
					)}
				</Box>
			</Grid>

			<Grid item xs={12}>
				<Box sx={{ height: 500, borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
					<Maps
						data={truckData}
						polygonLine={displayedRoute || routeData}
						id={moment().unix()}
					/>
				</Box>
			</Grid>
		</Grid>
	);
};

export default RouteHistoryMap;
